/**
 * Role-default presets, one arrangement per device mode (ADR-UI-001 decision 5).
 *
 * Presets are the source of every default and every reset target. Tablet and
 * mobile get their own fixed shells (order-driven stacking / tabs) rather than a
 * shrunk desktop layout. Feature lanes place real panels by registering content
 * under these ids; the ids here are the alpha placeholder set.
 */
import type { DeviceMode, PanelDescriptor, WorkspaceLayout, WorkspacePreset } from "./types";

const DEFAULT_CONSTRAINTS = { minWidth: 240, minHeight: 160 } as const;

/** Alpha panel identities. Feature lanes register content under these ids. */
export const DEFAULT_PANEL_IDS = ["situation", "resources", "activity"] as const;

function panel(
  id: string,
  slot: string,
  order: number,
  position: { x: number; y: number },
  size: { width: number; height: number },
): PanelDescriptor {
  return {
    id,
    slot,
    order,
    position,
    size,
    constraints: DEFAULT_CONSTRAINTS,
    collapsed: false,
    fullscreen: false,
  };
}

const desktopLayout: WorkspaceLayout = {
  schemaVersion: 2,
  presetId: "operations-desktop",
  panels: [
    panel("situation", "main", 0, { x: 24, y: 24 }, { width: 520, height: 380 }),
    panel("resources", "side", 1, { x: 564, y: 24 }, { width: 360, height: 180 }),
    panel("activity", "bottom", 2, { x: 564, y: 224 }, { width: 360, height: 180 }),
  ],
};

/** Stacked shells reuse the same panel identities; geometry is presentational. */
function stackedLayout(presetId: string): WorkspaceLayout {
  return {
    schemaVersion: 2,
    presetId,
    panels: [
      panel("situation", "main", 0, { x: 0, y: 0 }, { width: 320, height: 320 }),
      panel("resources", "main", 1, { x: 0, y: 0 }, { width: 320, height: 240 }),
      panel("activity", "main", 2, { x: 0, y: 0 }, { width: 320, height: 240 }),
    ],
  };
}

export const desktopOperationsPreset: WorkspacePreset = {
  id: "operations-desktop",
  deviceMode: "desktop",
  layout: desktopLayout,
};

export const tabletOperationsPreset: WorkspacePreset = {
  id: "operations-tablet",
  deviceMode: "tablet",
  layout: stackedLayout("operations-tablet"),
};

export const mobileOperationsPreset: WorkspacePreset = {
  id: "operations-mobile",
  deviceMode: "mobile",
  layout: stackedLayout("operations-mobile"),
};

const presetsByMode: Readonly<Record<DeviceMode, WorkspacePreset>> = {
  desktop: desktopOperationsPreset,
  tablet: tabletOperationsPreset,
  mobile: mobileOperationsPreset,
};

export interface RoleDefaultInput {
  readonly role: string;
  readonly deviceMode: DeviceMode;
}

/**
 * Resolves the role default for a device mode. `role` is part of the signature
 * so role-specific presets can be added later without a call-site change; the
 * alpha ships a single operations role.
 */
export function resolveRoleDefaultPreset(input: RoleDefaultInput): WorkspacePreset {
  return presetsByMode[input.deviceMode];
}

/** Sensible width-based default device mode (legacy UI_MODE pattern). */
export function detectDeviceMode(width: number): DeviceMode {
  if (width < 640) return "mobile";
  if (width < 1024) return "tablet";
  return "desktop";
}

/** SSR/jsdom-safe device-mode probe; defaults to desktop when width is unknown. */
export function detectDeviceModeFromWindow(): DeviceMode {
  if (typeof window === "undefined" || typeof window.innerWidth !== "number") return "desktop";
  return detectDeviceMode(window.innerWidth);
}
